import React, { forwardRef, useId } from 'react'

import { AlertCircle, Eye, EyeOff } from 'lucide-react'

import { cn } from '@/utils/cn'

// Type-safe input variants
const INPUT_VARIANTS = {
  default: 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
  error: 'border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50',
  success: 'border-green-300 focus:border-green-500 focus:ring-green-500'
} as const

const INPUT_SIZES = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-3 text-base',
  lg: 'h-12 px-4 text-lg'
} as const

export type InputVariant = keyof typeof INPUT_VARIANTS
export type InputSize = keyof typeof INPUT_SIZES

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  helperText?: string
  variant?: InputVariant
  size?: InputSize
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isRequired?: boolean
  showPasswordToggle?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'default',
      size = 'md',
      leftIcon,
      rightIcon,
      isRequired = false,
      showPasswordToggle = false,
      className,
      id,
      type = 'text',
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const generatedId = useId()
    const inputId = id || `input-${generatedId}`
    const errorId = `${inputId}-error`
    const helperId = `${inputId}-helper`

    // Determine the actual input type
    const inputType = showPasswordToggle && type === 'password' && showPassword ? 'text' : type

    // Determine variant based on error state
    const currentVariant = error ? 'error' : variant

    const baseInputStyles = [
      'block w-full rounded-md transition-colors',
      'placeholder:text-gray-400',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      'focus:outline-none focus:ring-1'
    ].join(' ')

    const getAriaDescribedBy = () => {
      const describedBy: string[] = []
      if (error) describedBy.push(errorId)
      if (helperText) describedBy.push(helperId)
      return describedBy.length > 0 ? describedBy.join(' ') : undefined
    }

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword)
    }

    const inputElement = (
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 text-sm" aria-hidden="true">
              {leftIcon}
            </span>
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          type={inputType}
          className={cn(
            baseInputStyles,
            INPUT_VARIANTS[currentVariant],
            INPUT_SIZES[size],
            leftIcon && 'pl-10',
            (rightIcon || showPasswordToggle) && 'pr-10',
            className
          )}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={getAriaDescribedBy()}
          aria-required={isRequired}
          {...props}
        />

        {(rightIcon || showPasswordToggle) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {showPasswordToggle && type === 'password' ? (
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                tabIndex={disabled ? -1 : 0}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            ) : (
              rightIcon && (
                <span className="text-gray-500 text-sm" aria-hidden="true">
                  {rightIcon}
                </span>
              )
            )}
          </div>
        )}
      </div>
    )

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium mb-1 transition-colors',
              error ? 'text-red-700' : 'text-gray-700',
              disabled && 'text-gray-500'
            )}
          >
            {label}
            {isRequired && (
              <span className="text-red-500 ml-1" aria-label="obligatorio">
                *
              </span>
            )}
          </label>
        )}

        {inputElement}

        {error && (
          <div id={errorId} className="mt-1 flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {helperText && !error && (
          <p id={helperId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
export { Input }
